import React from "react";
import {
  Box,
  Grid,
  LinearProgress,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";

import Header from "../../components/Header";
import DashboardCard from "../../components/DashboardCard";
import ThemedDataGrid from "../../components/ThemedDataGrid";
import StatusChip from "../../components/StatusChip";
import ActionButton from "../../components/ActionButton";

import { useResolvedTenantId } from "../../hooks/useResolvedTenantId";
import useStaffKnowledgeDashboard from "../../hooks/useStaffKnowledgeDashboard";

import {
  achievementTone,
  learningStatusTone,
} from "../../status/StatusChipTones";

const StaffKnowledgeDashboard = () => {
  const navigate = useNavigate();

  const {
    user,
    tenantId,
  } = useResolvedTenantId();

  const {
    data,
    loading,
    error,
  } = useStaffKnowledgeDashboard({
    tenantId,
    userId: user?.id,
  });

  if (!tenantId) {
    return (
      <Box>
        <Header
          title="MY LEARNING JOURNEY"
          subtitle="Select a tenant to view your learning progress"
        />
      </Box>
    );
  }

  if (loading) {
    return (
      <Box>
        <Header
          title="MY LEARNING JOURNEY"
          subtitle="Loading your progress..."
        />
      </Box>
    );
  }

  if (error || !data) {
    return (
      <Box>
        <Header
          title="MY LEARNING JOURNEY"
          subtitle="Failed to load your dashboard"
        />
      </Box>
    );
  }

  const rank = getKnowledgeRank(
    data.summary.understoodCount
  );

  const nextRank = getNextKnowledgeRank(
    data.summary.understoodCount
  );

  const nextAction = data.nextActionRows?.[0];

  const nextActionSopId =
    nextAction?.sopId ||
    nextAction?.projectId ||
    nextAction?.id;

  const sopColumns = [
    {
      field: "sopTitle",
      headerName: "SOP",
      flex: 1.4,
      minWidth: 200,
    },
    {
      field: "departmentName",
      headerName: "Department",
      flex: 1,
      minWidth: 150,
    },
    {
      field: "readStatus",
      headerName: "Read",
      flex: 0.7,
      minWidth: 110,
      renderCell: ({ value }) => (
        <LearningStatusChip status={value} />
      ),
    },
    {
      field: "understoodStatus",
      headerName: "Understood",
      flex: 0.9,
      minWidth: 140,
      renderCell: ({ value }) => (
        <LearningStatusChip status={value} />
      ),
    },
  ];

  return (
    <Box>
      <Header
        title="MY LEARNING JOURNEY"
        subtitle="Build your knowledge, track your progress, and keep your skills up to date"
      />

      <Grid container spacing={2}>
        <DashboardCard
          cardTitle="Your Progress"
          xs={12}
          md={12}
          height="220px"
        >
          <Box
            display="flex"
            flexDirection="column"
            justifyContent="center"
            height="100%"
            gap={1.5}
          >
            <Typography
              variant="h3"
              fontWeight="bold"
            >
              You are a {rank}
            </Typography>

            <Typography
              variant="h6"
              color="text.secondary"
            >
              You have understood{" "}
              {data.summary.understoodCount} of{" "}
              {data.summary.assignedCount} assigned SOPs.
            </Typography>

            <Box>
              <Box
                display="flex"
                justifyContent="space-between"
                mb={0.5}
              >
                <Typography
                  variant="body2"
                  color="text.secondary"
                >
                  Journey Progress
                </Typography>

                <Typography
                  variant="body2"
                  fontWeight="bold"
                >
                  {data.summary.progressPercent}%
                </Typography>
              </Box>

              <LinearProgress
                variant="determinate"
                value={data.summary.progressPercent}
                sx={{
                  height: 10,
                  borderRadius: 5,
                }}
              />
            </Box>

            <Typography
              variant="body2"
              color="text.secondary"
            >
              Keep going — each completed SOP increases
              what you can confidently cover in the
              business. Next level: {nextRank}.
            </Typography>
          </Box>
        </DashboardCard>

        <DashboardCard
          cardTitle="Current Level"
          xs={12}
          sm={6}
          md={3}
          height="140px"
        >
          <StatValue value={rank} small />
        </DashboardCard>

        <DashboardCard
          cardTitle="Learning Path"
          xs={12}
          sm={6}
          md={3}
          height="140px"
        >
          <StatValue
            value={data.summary.assignedCount}
          />
        </DashboardCard>

        <DashboardCard
          cardTitle="Knowledge Gained"
          xs={12}
          sm={6}
          md={3}
          height="140px"
        >
          <StatValue
            value={data.summary.understoodCount}
          />
        </DashboardCard>

        <DashboardCard
          cardTitle="Journey Progress"
          xs={12}
          sm={6}
          md={3}
          height="140px"
        >
          <StatValue
            value={`${data.summary.progressPercent}%`}
          />
        </DashboardCard>

        <DashboardCard
          cardTitle="Today's Mission"
          xs={12}
          md={6}
          height="300px"
        >
          {nextAction ? (
            <Box
              display="flex"
              flexDirection="column"
              gap={1.5}
              p={1}
            >
              <Typography
                variant="overline"
                color="text.secondary"
              >
                Next action
              </Typography>

              <Typography
                variant="h4"
                fontWeight="bold"
              >
                {nextAction.sopTitle}
              </Typography>

              <Typography
                variant="body1"
                color="text.secondary"
              >
                Department:{" "}
                {nextAction.departmentName ||
                  "Not assigned"}
              </Typography>

              <Box>
                <ActionButton
                  tone="success"
                  disabled={!nextActionSopId}
                  onClick={() =>
                    navigate(
                      `/sops/read/${nextActionSopId}`,
                      {
                        state: {
                          redirectPath:
                            "/staffDashboard",
                        },
                      }
                    )
                  }
                >
                  {nextAction.action || "Read SOP"}
                </ActionButton>
              </Box>

              <Typography
                variant="body2"
                color="text.secondary"
              >
                Complete this to move your learning
                journey forward.
              </Typography>
            </Box>
          ) : (
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              height="100%"
              gap={1}
            >
              <Typography
                variant="h4"
                fontWeight="bold"
              >
                🎉 You are fully up to date
              </Typography>

              <Typography
                variant="body2"
                color="text.secondary"
              >
                There are no outstanding learning actions
                right now.
              </Typography>
            </Box>
          )}
        </DashboardCard>

        <DashboardCard
          cardTitle="Badges Earned"
          xs={12}
          md={6}
          height="300px"
        >
          <AchievementPanel
            summary={data.summary}
          />
        </DashboardCard>

        <DashboardCard
          cardTitle="My Learning Records"
          xs={12}
          md={12}
          height="420px"
        >
          <ThemedDataGrid
            rows={data.sopRows}
            columns={sopColumns}
            hideFooter
            disableRowSelectionOnClick
          />
        </DashboardCard>
      </Grid>
    </Box>
  );
};

const StatValue = ({
  value,
  small = false,
}) => (
  <Box
    display="flex"
    alignItems="center"
    justifyContent="center"
    height="100%"
  >
    <Typography
      variant={small ? "h3" : "h2"}
      fontWeight="bold"
    >
      {value}
    </Typography>
  </Box>
);

const AchievementPanel = ({ summary }) => {
  const achievements = [
    {
      label: "First Step",
      icon: "👣",
      unlocked: summary.readCount >= 1,
    },
    {
      label: "Getting Started",
      icon: "📘",
      unlocked: summary.understoodCount >= 5,
    },
    {
      label: "Rookie",
      icon: "🌱",
      unlocked: summary.understoodCount >= 10,
    },
    {
      label: "Pro",
      icon: "⭐",
      unlocked: summary.understoodCount >= 25,
    },
    {
      label: "Master",
      icon: "🏆",
      unlocked: summary.understoodCount >= 50,
    },
    {
      label: "Guru",
      icon: "🧠",
      unlocked: summary.understoodCount >= 100,
    },
    {
      label: "Fully Up To Date",
      icon: "🎯",
      unlocked:
        summary.assignedCount > 0 &&
        summary.understoodCount ===
          summary.assignedCount,
    },
  ];

  return (
    <Box
      display="flex"
      flexWrap="wrap"
      gap={1.5}
      p={1}
    >
      {achievements.map((achievement) => (
        <Box
          key={achievement.label}
          sx={{
            opacity: achievement.unlocked
              ? 1
              : 0.35,
          }}
        >
          <StatusChip
            label={`${achievement.icon} ${achievement.label}`}
            tone={achievementTone(
              achievement.unlocked
            )}
          />
        </Box>
      ))}
    </Box>
  );
};

const LearningStatusChip = ({ status }) => (
  <StatusChip
    label={status || "Not started"}
    tone={learningStatusTone(status)}
  />
);

const getKnowledgeRank = (
  understoodCount = 0
) => {
  if (understoodCount >= 100) {
    return "Guru";
  }

  if (understoodCount >= 50) {
    return "Master";
  }

  if (understoodCount >= 25) {
    return "Pro";
  }

  if (understoodCount >= 10) {
    return "Rookie";
  }

  return "New Starter";
};

const getNextKnowledgeRank = (
  understoodCount = 0
) => {
  if (understoodCount >= 100) {
    return "Legend";
  }

  if (understoodCount >= 50) {
    return "Guru";
  }

  if (understoodCount >= 25) {
    return "Master";
  }

  if (understoodCount >= 10) {
    return "Pro";
  }

  return "Rookie";
};

export default StaffKnowledgeDashboard;